import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import Icon from '../Icon';
import AvatarStatus, { AVATAR_STATUS } from './AvatarStatus';
import { generateColorGradient, getInitialPair, getInitialPairFromParts } from './avatarColor';

// Design System — Avatar
// A circular user avatar. Reused on its own and inside AvatarGroup.
//   variant: placeholder | initials | image | icon
//            image falls back to initials (or the placeholder without a name) when
//            `src` is missing or fails to load.
//   size:    xs (24) · sm (32) · md (40) · lg (48) · xl (56) · 2xl (64)
//   status:  AVATAR_STATUS.ONLINE | TALENT_GEM | ANONYMOUS
// Pass `firstName`+`lastName` when you have the separate fields (exact parity
// with the legacy portrait, even for multi-word first names), or a single
// `name` ("Shoaib Ahmed" → "SA") as the convenience path. `initials` overrides
// only the DISPLAYED letters — the background color always derives from the
// name fields, picked from the same 17-gradient palette the Applications table
// uses (avatarColor.js), so the Avatar matches the person's legacy portrait.
// Styles: ./index.less is wired into less/ats_style.less (the white-label
// pipeline) — no JSX .less import, per frontend-structure.mdc.

const SIZES = ['xs', 'sm', 'md', 'lg', 'xl', '2xl'];
const VARIANTS = ['placeholder', 'initials', 'image', 'icon'];
const STATUSES = Object.values(AVATAR_STATUS);

const Avatar = ({
    variant = 'placeholder',
    size = 'md',
    name = null,
    firstName = null,
    lastName = null,
    initials = null,
    src = null,
    alt = null,
    icon = 'user-01',
    tinted = false,
    status = null,
    statusTooltip = null,
    className = null,
    onClick = null,
    ...rest
}) => {
    // Separate fields win (exact legacy parity); otherwise split the full name.
    // The `initials` display override never feeds the color seed — it only
    // stands in when no name at all is available.
    const hasParts = firstName != null || lastName != null;
    const seedPair = hasParts
        ? getInitialPairFromParts(firstName, lastName)
        : getInitialPair(name || initials || '');
    const displayInitials = initials || seedPair.join('').trim();
    const fullName = name || [firstName, lastName].filter(Boolean).join(' ') || null;
    const isAnonymous = status === AVATAR_STATUS.ANONYMOUS;
    const [failedSrc, setFailedSrc] = useState(null);
    const hasName = Boolean(fullName || initials);
    const imageUsable = Boolean(src) && failedSrc !== src;
    const shownVariant = variant === 'image' && !imageUsable ? (hasName ? 'initials' : 'placeholder') : variant;

    // Only initials avatars get the generated color (unless tinted or anonymous).
    const isColored = shownVariant === 'initials' && !tinted && !isAnonymous;
    const colorStyle = isColored ? { background: generateColorGradient(...seedPair) } : undefined;

    const sizeClass = SIZES.includes(size) ? `avatar--${size}` : 'avatar--md';

    const renderContent = () => {
        if (isAnonymous) return <Icon value="incognito" noMargin />;
        if (shownVariant === 'image') {
            return (
                <img
                    className="avatar__image"
                    src={src}
                    alt={alt ?? fullName ?? ''}
                    loading="lazy"
                    onError={() => setFailedSrc(src)}
                />
            );
        }
        if (shownVariant === 'initials') return <span className="avatar__initials">{displayInitials}</span>;
        if (shownVariant === 'icon') return <Icon value={icon} noMargin />;
        return <Icon value="user-01" noMargin />; // placeholder
    };

    // Keyboard/SR users need a real button when the avatar is interactive.
    const clickable = typeof onClick === 'function';
    const Tag = clickable ? 'button' : 'div';

    return (
        <Tag
            {...(clickable ? { type: 'button', 'aria-label': fullName || undefined } : {})}
            className={classNames('avatar', sizeClass, className, {
                'avatar--placeholder': shownVariant === 'placeholder' && !isAnonymous,
                'avatar--colored': isColored,
                'avatar--tinted': tinted && !isAnonymous,
                'avatar--anonymous': isAnonymous,
                'avatar--clickable': clickable,
            })}
            style={colorStyle}
            onClick={clickable ? onClick : undefined}
            {...rest}
        >
            {renderContent()}
            {status && !isAnonymous && <AvatarStatus status={status} tooltip={statusTooltip} />}
        </Tag>
    );
};

Avatar.propTypes = {
    /** Visual variant; `image` falls back to initials when the photo is missing or broken */
    variant: PropTypes.oneOf(VARIANTS),
    size: PropTypes.oneOf(SIZES),
    name: PropTypes.string,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    initials: PropTypes.string,
    src: PropTypes.string,
    alt: PropTypes.string,
    icon: PropTypes.string,
    tinted: PropTypes.bool,
    status: PropTypes.oneOf(STATUSES),
    statusTooltip: PropTypes.string,
    className: PropTypes.string,
    onClick: PropTypes.func,
};

export default Avatar;
